import React, { FC, useState, ReactNode } from 'react';

import { Row } from 'Shared/components';
import { booleanValueMapping, isPresent } from 'Shared/utils';
import { CommandType } from 'QuoteApp/services/endpoints/GetDetails';
import { ChallengeType } from 'QuoteApp/services/endpoints/PostChallengeAccident';
import { useEdit } from 'QuoteApp/utils/contexts';
import { ChallengeButton } from '../ChallengeButton';
import { ChallengeIcon } from '../ChallengeIcon';
import styles from './styles.module.css';
import sharedStyles from '../styles/reportStyles.module.css';
import classNames from 'classnames';

type ReportDetailRowProps = {
  canChallenge?: boolean;
  challengeCommand?: CommandType;
  challengeForm: React.ReactNode;
  identifier: string;
  includeEdit?: boolean;
  includeRemove?: boolean;
  children?: React.ReactNode;
  onEdit?: () => void;
  onRemove?: () => void;
  source: string;
  type:
    | 'accidents'
    | 'lending-loss'
    | 'driver-license'
    | 'current-carrier'
    | 'violations'
    | 'prior-policies';
  checkmarked?: boolean | null;
  claimStatus?: string | null;
  shortSource?: boolean;
};

export const getDisplayValue = (data: ReactNode) => {
  if (typeof data === 'string' && (data === 'true' || data === 'false')) {
    return booleanValueMapping[data];
  }
  if (typeof data !== 'undefined' && data !== null) {
    return data;
  }
  return '—';
};

const reportSource = (source: string, short?: boolean) => {
  const sources: Record<string, string> = {
    user_reported: 'User',
    agent_disputed: short ? 'AD' : 'Agent disputed',
    MVR: 'MVR',
    CLUE: 'CLUE',
    NCF: 'NCF',
    ADPF: short ? 'ADPF' : 'Auto Data Prefill',
  };
  return sources[source] || (short ? 'TPR' : 'Third-party report');
};

export const ReportDetailRow: FC<ReportDetailRowProps> = ({
  canChallenge,
  challengeCommand,
  challengeForm,
  children,
  identifier,
  includeEdit = true,
  includeRemove,
  onEdit,
  onRemove,
  source,
  type,
  checkmarked = true,
  claimStatus = null,
  shortSource = false,
}) => {
  const { editingId, changeEditingId } = useEdit();
  const [challengeType, changeChallengeType] = useState<ChallengeType>('edit');

  return (
    <li className={styles.wrapper} data-testid={`${type}-report-detail-row`}>
      <div className={styles.row}>
        {checkmarked !== null && (
          <div className={styles.checkSpacer}>
            <ChallengeIcon challenged={!checkmarked} />
          </div>
        )}
        {claimStatus !== null && (
          <div className={styles.claimStatusSpacer}>{claimStatus}</div>
        )}
        <div
          className={classNames(
            styles.source,
            shortSource && styles.shortSource
          )}
        >
          {reportSource(source, shortSource)}
        </div>
        <Row className={styles.content}>{children}</Row>
        {canChallenge && (includeEdit || includeRemove) && (
          <div
            className={
              includeRemove
                ? sharedStyles.largeSpacer
                : sharedStyles.smallSpacer
            }
          >
            {includeEdit && challengeCommand?.runnable && (
              <ChallengeButton
                onClick={() => {
                  changeEditingId(identifier);
                  changeChallengeType('edit');
                  onEdit && onEdit();
                }}
                variant="edit"
                disabled={
                  isPresent(editingId) &&
                  (challengeType === 'edit' || editingId !== identifier)
                }
                className={sharedStyles.challengeButton}
                dataTestId={`edit-${type}-${identifier}-button`}
              />
            )}
            {includeRemove && challengeCommand?.runnable && (
              <ChallengeButton
                onClick={() => {
                  changeEditingId(identifier);
                  changeChallengeType('remove');
                  onRemove && onRemove();
                }}
                variant="remove"
                disabled={
                  isPresent(editingId) &&
                  (challengeType === 'remove' || editingId !== identifier)
                }
                dataTestId={`remove-${type}-${identifier}-button`}
              />
            )}
          </div>
        )}
      </div>
      {editingId === identifier ? (
        <div className={styles.collapsibleContent}>{challengeForm}</div>
      ) : null}
    </li>
  );
};
